import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserRole } from '../enums/role-name';
import { StorageKeys, StorageService } from '../services/storage.service';
import { ConsultingTeamMemberRole } from '../types/consulting-team-member';

export type Role = UserRole | ConsultingTeamMemberRole;

@Injectable({
  providedIn: 'root',
})
export class UserRoleStore {
  private currentUserRole: Role | null = this.storageService.getItem(StorageKeys.CURRENT_USER_ROLE) as Role;
  private currentUserRoleSubj$: BehaviorSubject<Role> = new BehaviorSubject<Role>(null!);

  currentUserRole$ = this.currentUserRoleSubj$.asObservable();

  private currentFacilityId: string | null = null;
  private currentEnterpriseId: string | null = null;

  private facilityRoles: { [facilityId: string]: string } = {};
  private enterpriseRoles: { [enterpriseId: string]: string } = {};

  constructor(private storageService: StorageService) {}

  setCurrentUserRole(userRole: Role): void {
    if (userRole) {
      this.currentUserRole = userRole;
      this.storageService.setItem(StorageKeys.CURRENT_USER_ROLE, userRole);
    } else {
      this.currentUserRole = null;
      this.storageService.removeItem(StorageKeys.CURRENT_USER_ROLE);
    }

    this.currentUserRoleSubj$.next(userRole);
  }

  getCurrentUserRole(): Role | null {
    return this.currentUserRole;
  }

  setCurrentEnterpriseId(enterpriseId: string | null): void {
    this.currentEnterpriseId = enterpriseId;
    this.updateUserRole();
  }

  setCurrentFacilityId(facilityId: string | null): void {
    this.currentFacilityId = facilityId;
    this.updateUserRole();
  }

  setFacilityRoles(roles: { [facilityId: string]: string }): void {
    this.facilityRoles = roles;
    this.updateUserRole();
  }

  setEnterpriseRoles(roles: { [enterpriseId: string]: string }): void {
    this.enterpriseRoles = roles;
    this.updateUserRole();
  }

  private updateUserRole(): void {
    if (this.currentEnterpriseId) {
      const enterpriseRole = this.enterpriseRoles[this.currentEnterpriseId];
      if (enterpriseRole) {
        this.setCurrentUserRole(enterpriseRole as Role);
        return;
      }
    }

    if (this.currentFacilityId) {
      const facilityRole = this.facilityRoles[this.currentFacilityId];
      if (facilityRole) {
        this.setCurrentUserRole(facilityRole as Role);
        return;
      }
    }
  }

  clear(): void {
    this.storageService.removeItem(StorageKeys.CURRENT_USER_ROLE);
    this.currentUserRoleSubj$.next(null!);
  }
}
